/**
 * Thin wrapper around the Storage Access API (https://developer.mozilla.org/docs/Web/API/Storage_Access_API).
 *
 * Inside a third-party iframe, browsers partition storage by the top-level (merchant) site, so the
 * Vipps origin gets a different bucket on every merchant. The classic `requestStorageAccess()` form
 * grants access to the *unpartitioned* first-party cookies for the iframe origin — the one surface
 * supported across Chrome, Safari and Firefox — which lets us reach a shared `.vipps.no` cookie.
 *
 * `requestStorageAccess()` MUST be called during transient activation (a user gesture).
 */

// Once granted in a page it stays granted; memoise so we don't re-request needlessly.
let sessionGranted = false;

export const canAttempt = (): boolean =>
  typeof document !== 'undefined' && typeof document.requestStorageAccess === 'function';

export const hasAccess = async (): Promise<boolean> => {
  if (typeof document === 'undefined' || typeof document.hasStorageAccess !== 'function') {
    return false;
  }
  try {
    return await document.hasStorageAccess();
  } catch {
    return false;
  }
};

const hasUserActivation = (): boolean =>
  typeof navigator === 'undefined' ||
  navigator.userActivation === undefined ||
  navigator.userActivation.isActive;

/**
 * Must be invoked synchronously inside a user-gesture handler. Resolves to whether access is granted.
 * Firefox/Safari reject (and log) `requestStorageAccess()` when called without transient activation,
 * so we never call it gesture-free — we bail early instead. A grant obtained earlier in the document
 * is memoised and reused by later (post-await) calls.
 */
export const requestAccess = async (): Promise<boolean> => {
  if (sessionGranted) {
    return true;
  }
  if (!canAttempt() || !hasUserActivation()) {
    return false;
  }
  try {
    await document.requestStorageAccess();
    sessionGranted = true;
    return true;
  } catch {
    return false;
  }
};

/** Test seam — module memo persists across tests otherwise. */
export const __resetForTests = (): void => {
  sessionGranted = false;
};
